import React, { FunctionComponent, useEffect, useState } from 'react'
import {
  Button,
  Card,
  Flag,
  ToastNotifications,
} from '@lightspeed/design-system-react'
import { useT, T } from '@transifex/react'
import { usePMOFFeature } from '@vend/business-components'
import { getRetailer } from '@vend/utilities'
import { capitaliseFirst } from '@vend/utilities/dist/strings/capitaliseFirst'
import { useAppSelector } from '../../../../state/hooks'
import {
  selectCustomer,
  selectSaleId,
  selectSalePayments,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import { selectTrainingModeEnabled } from '../../../../state/slices/posState/posStateSlice'
import { isValidCustomer } from '../../../../functions/sale'
import { getFeatureFromCache } from '../../../../util/feature-cache'
import {
  useRegisterManager,
  useUserManager,
  useWebRegisterStatusManager,
} from '../../../../ng-compat/hooks'
import { PERMISSIONS } from '../../../../constants'
import { useFetchPairing } from '../../../payments/lspay-payment-modal/hooks/useFetchPairing'
import { Payment } from '../../../../models/payment'
import { TerminalCardSaveModal } from './TerminalCardSaveModal'

type SaveState = 'idle' | 'saving' | 'succeeded'

const BANNER_DISMISSED_KEY = 'terminal_card_save_banner_dismissed'
const CARD_DOTS = '\u2022\u2022\u2022\u2022'

/**
 * Find the first LSPay terminal payment on the sale that has card data.
 */
function findLspayTerminalPayment(payments: Payment[]): Payment | undefined {
  return payments.find(
    p => p.card?.brand && p.card?.lastFour && p.card?.entryMethod !== 'KEYED'
  )
}

export const TerminalCardSave: FunctionComponent = () => {
  const t = useT()
  const customer = useAppSelector(selectCustomer)
  const saleId = useAppSelector(selectSaleId)
  const payments = useAppSelector(selectSalePayments)
  const isTrainingMode = useAppSelector(selectTrainingModeEnabled)
  const registerManager = useRegisterManager()
  const userManager = useUserManager()
  const loggedInUserId = userManager?.getLoggedInUser()?.id
  const webRegisterStatusManager = useWebRegisterStatusManager()
  const { isEnabled: pmofSettingEnabled } = usePMOFFeature()

  const featureFlagEnabled =
    getFeatureFromCache('enable_customer_saved_cards') &&
    getFeatureFromCache('lspay_terminal_save_cards')
  const userPermissionGranted = userManager?.hasPermission(
    PERMISSIONS.LSPAY_PMOF_ADD_EDIT_USE
  )

  const register = registerManager?.getActiveRegister()
  const { pairing } = useFetchPairing(register?.id)

  const [saveState, setSaveState] = useState<SaveState>('idle')
  const [retailerId, setRetailerId] = useState<string | null>(null)
  const [bannerDismissed, setBannerDismissed] = useState(
    () => localStorage.getItem(BANNER_DISMISSED_KEY) === 'true'
  )

  const dismissBanner = () => {
    setBannerDismissed(true)
    localStorage.setItem(BANNER_DISMISSED_KEY, 'true')
  }

  useEffect(() => {
    getRetailer()
      .then(retailer => {
        setRetailerId(retailer.id)
      })
      .catch(() => {
        // retailerId stays null, component won't render save card UI
      })
  }, [])

  const terminalPayment = findLspayTerminalPayment(payments)

  const enabled =
    featureFlagEnabled &&
    pmofSettingEnabled &&
    userPermissionGranted &&
    !isTrainingMode &&
    webRegisterStatusManager?.isOnline() // Saving a card requires calling the swiper API

  if (!enabled || !isValidCustomer(customer) || !retailerId) {
    return null
  }
  if (!terminalPayment?.card?.brand || !terminalPayment?.card?.lastFour) {
    return null
  }

  if (!pairing?.deviceID || !pairing?.pli) {
    return null
  }

  if (saveState === 'succeeded') {
    return null
  }

  const cardBrand = capitaliseFirst(terminalPayment.card.brand)
  const cardLastFour = terminalPayment.card.lastFour
  const customerName = customer.name || customer.first_name || ''

  const handleSuccess = () => {
    setSaveState('succeeded')
    ToastNotifications.success(
      t("{cardBrand} {dots}{cardLastFour} added to {customerName}'s profile", {
        cardBrand,
        dots: CARD_DOTS,
        cardLastFour,
        customerName,
        _context: 'Success toast when card is saved on file',
      })
    )
  }

  const handleError = (message: string) => {
    setSaveState('idle')
    ToastNotifications.negative(
      message ||
        t('Card save failed', {
          _context: 'Error toast when card save fails',
        })
    )
  }

  const handleCancelled = () => {
    setSaveState('idle')
    ToastNotifications.negative(
      t('Card save cancelled', {
        _context: 'Toast when card save is cancelled by customer on terminal',
      })
    )
  }

  return (
    <>
      {/* TODO: Replace with Helios 2.0 "Notice" component when available */}
      <Card className="vd-mt10">
        {!bannerDismissed && (
          <>
            <div
              className="vd-pa5 vd-flex vd-flex--align-start"
              style={{
                backgroundColor: '#f5f8ff',
                borderRadius: '8px 8px 0 0',
              }}
            >
              <span style={{ flexShrink: 0 }} className="vd-mr2">
                <Flag
                  content={t('New', {
                    _context: 'Badge for new terminal card save feature',
                  })}
                  category="info"
                />
              </span>
              <div style={{ flex: 1, textAlign: 'left' }}>
                <div className="vd-text--bold">
                  <T
                    _str="Save cards for faster checkouts"
                    _context="Title for terminal card save info banner"
                  />
                </div>
                <div className="vd-text--secondary vd-text--sub">
                  <T
                    _str="Securely save cards for your regulars to process future orders with a single click. You can turn this off anytime in Settings."
                    _context="Description for terminal card save info banner"
                  />
                </div>
              </div>
              <button
                className="vd-btn--reset vd-ml4"
                style={{ flexShrink: 0 }}
                onClick={dismissBanner}
                aria-label={t('Dismiss', {
                  _context: 'Dismiss terminal card save banner',
                })}
              >
                &times;
              </button>
            </div>
            <hr
              style={{
                border: 'none',
                borderTop: '2px solid #a8c4f5',
                margin: 0,
              }}
            />
          </>
        )}
        <div className="vd-ma5 vd-flex vd-flex--align-center vd-flex--justify-between">
          <span>
            <T
              _str="Save card for future purchases"
              _context="Label for save card on file on the confirmation screen"
            />
          </span>
          <Button
            variant="supplementary"
            onClick={() => setSaveState('saving')}
          >
            <T
              _str="Save card"
              _context="Button to initiate saving card on file via terminal"
            />
          </Button>
        </div>
      </Card>
      {saveState === 'saving' && (
        <TerminalCardSaveModal
          merchantReference={retailerId}
          customerReference={customer.id}
          customerName={customerName}
          pspTerminalId={pairing.deviceID}
          pli={pairing.pli}
          cardBrand={cardBrand}
          cardLastFour={cardLastFour}
          saleId={saleId || undefined}
          createdBy={loggedInUserId}
          metadata={{
            salesChannel: 'IN_STORE',
            deviceId: register?.id || '',
          }}
          onSuccess={handleSuccess}
          onError={handleError}
          onCancelled={handleCancelled}
        />
      )}
    </>
  )
}
